import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ShieldCheck, FileLock2, Download, Loader2, ShieldX, CheckCircle2, Info } from "lucide-react";
import { Button } from "../ui/button";
import { LanguageSelector } from "../LanguageSelector";
import { decryptManifest, decryptSecureFile } from "../../lib/secureFiles/crypto";
import { readKeyFromHash, sanitizeFilename, triggerLocalDownload, formatBytes } from "../../lib/secureFiles/link";
import { fetchSecureFileMeta, downloadSecureFileCiphertext, secureFileErrorCode } from "../../lib/secureFiles/api";
import { intlLocaleFor } from "../../i18n";

// Referrer-Policy: no-referrer pour la page publique (en plus de l'en-tête serveur sur les API).
const useNoReferrer = () => useEffect(() => {
  const m = document.createElement("meta");
  m.name = "referrer";
  m.content = "no-referrer";
  document.head.appendChild(m);
  return () => m.remove();
}, []);

const Wrap = ({ children }) => (
  <div className="min-h-screen bg-[#080B10] flex items-start justify-center px-4 py-8">
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="w-full max-w-md" data-testid="secure-file-receive-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-emerald-400" />
          <span className="font-heading font-bold text-white text-xl">SecurePass</span></div>
        <LanguageSelector />
      </div>
      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8">{children}</div>
    </motion.div>
  </div>
);

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-800 last:border-0 text-sm">
    <span className="text-slate-400 shrink-0">{label}</span>
    <span data-testid={testid} className="text-slate-100 text-right break-all">{value}</span>
  </div>
);

// /f/:token#k=<clé> — la clé n'est lue que depuis location.hash et ne quitte jamais le navigateur.
export default function SecureFileReceivePage() {
  useNoReferrer();
  const { token } = useParams();
  const { t, i18n } = useTranslation("files");
  const lng = i18n.resolvedLanguage;
  const keyRef = useRef(null);
  const metaRef = useRef(null);
  const [state, setState] = useState("loading");
  const [manifest, setManifest] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [error, setError] = useState(null);
  const [hash, setHash] = useState(() => window.location.hash);

  // Une clé corrigée dans le fragment (#k=…) relance la vérification locale sans rechargement.
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    setState("loading");
    setError(null);
    const key = readKeyFromHash(hash);
    if (!key) { setError("MISSING_KEY"); setState("error"); return; }
    keyRef.current = key;
    let stale = false;  // une vérification lancée avec une ancienne clé ne doit pas écraser l'état courant
    (async () => {
      let meta;
      try { meta = await fetchSecureFileMeta(token); } catch (e) { if (!stale) { setError(secureFileErrorCode(e)); setState("error"); } return; }
      let m;
      try { m = await decryptManifest(meta, key); }  // échec => STOP : jamais d'appel au download
      catch (_) { if (!stale) { setError("WRONG_KEY"); setState("error"); keyRef.current = null; } return; }
      if (stale) return;
      setManifest(m);
      metaRef.current = meta;
      setExpiresAt(meta.expires_at);
      setState("ready");
    })();
    return () => { stale = true; };
  }, [token, hash]);

  const download = async () => {
    setState("downloading");
    let ct;
    try { ct = await downloadSecureFileCiphertext(token); } catch (e) { setError(secureFileErrorCode(e)); setState("error"); return; }
    setState("decrypting");
    try {
      let plain = await decryptSecureFile(metaRef.current, ct, keyRef.current);
      triggerLocalDownload(plain, manifest.name);
      plain = null;
      setState("done");
    } catch (_) { setError("INTEGRITY"); setState("error"); }
    ct = null;
    keyRef.current = null;  // nettoyage raisonnable des références (aucune garantie d'effacement mémoire JS)
    metaRef.current = null;
  };

  const date = (v) => new Intl.DateTimeFormat(intlLocaleFor(lng), { dateStyle: "medium", timeStyle: "short" }).format(new Date(v));

  if (state === "loading") {
    return <Wrap><div data-testid="sf-recv-loading" className="flex items-center gap-2 text-slate-300 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{t("recipient.loading")}</div></Wrap>;
  }
  if (state === "error") {
    return (
      <Wrap>
        <div className="flex items-center gap-2 mb-3"><ShieldX className="w-5 h-5 text-rose-400" /><h1 className="font-heading text-xl font-bold text-white">{t("recipient.title")}</h1></div>
        <p data-testid="sf-recv-error" data-code={error} role="alert" className="text-sm text-rose-200">{t(`errors.${error}`, { defaultValue: t("errors.UNKNOWN") })}</p>
      </Wrap>
    );
  }
  if (state === "done") {
    return (
      <Wrap>
        <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="w-5 h-5 text-emerald-400" /><h1 data-testid="sf-recv-done" className="font-heading text-xl font-bold text-white">{t("recipient.done")}</h1></div>
        <p className="text-sm text-slate-300">{t("recipient.doneSub")}</p>
      </Wrap>
    );
  }
  const busy = state === "downloading" || state === "decrypting";
  return (
    <Wrap>
      <div className="flex items-center gap-2 mb-4"><FileLock2 className="w-5 h-5 text-emerald-400" /><h1 className="font-heading text-xl font-bold text-white">{t("recipient.title")}</h1></div>
      <div data-testid="sf-recv-info" className="mb-5">
        <Row label={t("recipient.fileName")} value={sanitizeFilename(manifest.name)} testid="sf-recv-name" />
        {manifest.mime && <Row label={t("recipient.fileType")} value={manifest.mime} testid="sf-recv-mime" />}
        <Row label={t("recipient.size")} value={formatBytes(manifest.size, lng)} testid="sf-recv-size" />
        <Row label={t("recipient.expires")} value={date(expiresAt)} testid="sf-recv-expires" />
      </div>
      <Button data-testid="sf-recv-download-btn" disabled={busy} onClick={download} className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
        {state === "downloading" ? t("recipient.downloading") : state === "decrypting" ? t("recipient.decrypting") : t("recipient.cta")}
      </Button>
      <p className="text-xs text-slate-400 mt-2">{t("recipient.ctaSub")}</p>
      <p data-testid="sf-recv-malware" className="mt-5 flex gap-2 text-xs text-slate-400 border-t border-slate-800 pt-4">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{t("recipient.malware")}</span>
      </p>
      <p className="mt-2 text-[11px] text-slate-500">{t("recipient.zk")}</p>
    </Wrap>
  );
}
